import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import { FlowNote } from '@/features/monitoring/FlowNote';
import {
  Badge,
  Button,
  Card,
  CardContent,
  EmptyState,
  FilterSelect,
  LoadingState,
  Modal,
  Pagination,
  ServerSearchInput,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TenantSelector,
} from '@/components/ui';
import { okdocLandingsApi } from '@/api/okdocLandings';
import { OkdocLandingDefinitionEditor } from '@/features/okdoc/admin/OkdocLandingDefinitionEditor';
import { OkdocLandingDefinitionPreview } from '@/features/okdoc/admin/OkdocLandingDefinitionPreview';
import {
  OKDOC_SCENARIO_KIND_OPTIONS,
  OKDOC_SOURCE_CONTEXT_OPTIONS,
  getOkdocScenarioKindLabel,
  getOkdocSourceContextLabel,
} from '@/features/okdoc/registry/scenarioRegistry';
import { useToast } from '@/hooks/useToast';
import { useTenantScope } from '@/hooks/useTenantScope';
import type {
  OkdocLandingCatalogItem,
  OkdocLandingDefinition,
  OkdocLandingPreviewResponse,
} from '@/types/okdocLandings';
import {
  OKDOC_LANDING_BINDING_SCOPE_LABELS,
  OKDOC_LANDING_ORIGIN_LABELS,
  OKDOC_LANDING_RESOLUTION_SOURCE_LABELS,
} from '@/types/okdocLandings';
import { OKDOC_FLOW_LABELS } from '@/types/okdocTracking';
import { formatDateTime } from '@/utils/formatters';
import { Eye, FlaskConical, Pencil } from 'lucide-react';

const PAGE_SIZE = 25;

const originTypeOptions = Object.entries(OKDOC_LANDING_ORIGIN_LABELS).map(([value, label]) => ({ value, label }));
const bindingScopeOptions = Object.entries(OKDOC_LANDING_BINDING_SCOPE_LABELS).map(([value, label]) => ({ value, label }));
const flowOptions = Object.entries(OKDOC_FLOW_LABELS).map(([value, label]) => ({ value, label: String(label) }));

function getBindingTargetCopy(item: Pick<OkdocLandingCatalogItem, 'template_name' | 'cluster_name'>) {
  if (item.template_name || item.cluster_name) {
    return {
      primary: item.template_name || '—',
      secondary: item.cluster_name || 'Без cluster binding',
    };
  }
  return {
    primary: 'Preview-only binding',
    secondary: 'Будет привязано позже',
  };
}

function BindingTargetDetails({ item }: { item: Pick<OkdocLandingCatalogItem, 'template_name' | 'cluster_name'> }) {
  const bindingTarget = getBindingTargetCopy(item);
  return (
    <div className="space-y-1 text-sm">
      <div>{bindingTarget.primary}</div>
      <div className="text-xs text-neutral-500">{bindingTarget.secondary}</div>
    </div>
  );
}

export function OkdocLandingsPageContent() {
  const { showError, showSuccess } = useToast();
  const { tenantId, isSuperAdmin, selectedTenantId, setSelectedTenant } = useTenantScope();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<OkdocLandingCatalogItem[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [flow, setFlow] = useState('');
  const [scenarioKind, setScenarioKind] = useState('');
  const [sourceContext, setSourceContext] = useState('');
  const [originType, setOriginType] = useState('');
  const [bindingScope, setBindingScope] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [preview, setPreview] = useState<OkdocLandingPreviewResponse | null>(null);
  const [previewKey, setPreviewKey] = useState<string | null>(null);
  const [editorLoading, setEditorLoading] = useState(false);
  const [editorSaving, setEditorSaving] = useState(false);
  const [editingItem, setEditingItem] = useState<OkdocLandingCatalogItem | null>(null);
  const [editingDefinition, setEditingDefinition] = useState<OkdocLandingDefinition | null>(null);
  const [testChannel, setTestChannel] = useState('sms');
  const [testSending, setTestSending] = useState(false);
  const [testError, setTestError] = useState<string | null>(null);
  const [testMessage, setTestMessage] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const previewParam = searchParams.get('preview') || '';

  useEffect(() => {
    setOffset(0);
  }, [search, flow, scenarioKind, sourceContext, originType, bindingScope, tenantId]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const response = await okdocLandingsApi.list({
          tenant_id: tenantId || undefined,
          search: search || undefined,
          flow: flow || undefined,
          scenario_kind: scenarioKind || undefined,
          source_context: sourceContext || undefined,
          origin_type: originType || undefined,
          binding_scope: bindingScope || undefined,
          limit: PAGE_SIZE,
          offset,
        });
        if (cancelled) return;
        setItems(response.items || []);
        setTotal(response.total || 0);
      } catch {
        if (!cancelled) {
          setItems([]);
          setTotal(0);
          showError('Не удалось загрузить каталог OKDoc landings');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [tenantId, search, flow, scenarioKind, sourceContext, originType, bindingScope, offset, reloadToken, showError]);

  const openPreview = async (key: string) => {
    setTestError(null);
    setTestMessage(null);
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set('preview', key);
      return next;
    });
    setPreviewKey(key);
    setPreviewLoading(true);
    setPreview(null);
    try {
      const response = await okdocLandingsApi.preview(key);
      setPreview(response);
    } catch {
      showError('Не удалось загрузить preview landing');
    } finally {
      setPreviewLoading(false);
    }
  };

  const openEditor = async (item: OkdocLandingCatalogItem) => {
    if (!item.definition_id) {
      showError('У landing не найден definition_id');
      return;
    }

    setEditingItem(item);
    setEditingDefinition(null);
    setEditorLoading(true);
    try {
      const response = await okdocLandingsApi.getDefinition({
        tenant_id: item.tenant_id,
        definition_id: item.definition_id,
      });
      setEditingDefinition(response.definition);
    } catch {
      showError('Не удалось загрузить definition для редактирования');
      setEditingItem(null);
    } finally {
      setEditorLoading(false);
    }
  };

  const saveDefinition = async () => {
    if (!editingItem || !editingDefinition) {
      return;
    }

    setEditorSaving(true);
    try {
      const response = await okdocLandingsApi.updateDefinition({
        tenant_id: editingItem.tenant_id,
        definition_id: editingDefinition.id,
        name: editingDefinition.name,
        prompt_text: editingDefinition.prompt_text,
        scenario_profile: editingDefinition.scenario_profile,
      });
      setEditingDefinition(response.definition);
      setReloadToken((current) => current + 1);
      if (preview?.item?.definition_id === response.definition.id && preview.item.tenant_id === editingItem.tenant_id) {
        setPreview({
          ...preview,
          scenario_profile: response.definition.scenario_profile,
          item: {
            ...preview.item,
            name: response.definition.name,
            prompt_text: response.definition.prompt_text,
            updated_at: response.definition.updated_at,
          },
        });
      }
      showSuccess('Лендинг успешно сохранён');
    } catch {
      showError('Не удалось сохранить definition');
      return;
    } finally {
      setEditorSaving(false);
    }
  };

  useEffect(() => {
    if (!previewParam || previewParam === previewKey) {
      return;
    }
    void openPreview(previewParam);
  }, [previewParam]);

  useEffect(() => {
    if (!preview?.test_channels?.length) {
      setTestChannel('sms');
      return;
    }
    const firstAvailable = preview.test_channels.find((item) => item.available);
    setTestChannel(firstAvailable?.channel || preview.test_channels[0]?.channel || 'sms');
    setTestError(null);
    setTestMessage(null);
  }, [preview?.item?.key, preview?.test_channels]);

  const sendTestLanding = async () => {
    if (!preview?.item?.key || !testChannel) {
      return;
    }

    setTestSending(true);
    setTestError(null);
    setTestMessage(null);
    try {
      const response = await okdocLandingsApi.sendTest({
        key: preview.item.key,
        channel: testChannel,
      });
      if (response.success) {
        setTestMessage(response.message);
        showSuccess(response.message);
      } else {
        const message = response.error || response.message || 'Не удалось отправить тестовый OKDoc-линк';
        setTestError(message);
        showError(message);
      }
    } catch (error: unknown) {
      const data = (error as { response?: { data?: { error?: string; message?: string } } })?.response?.data;
      const message =
        (typeof data?.message === 'string' && data.message) ||
        (typeof data?.error === 'string' && data.error) ||
        'Не удалось отправить тестовый OKDoc-линк';
      setTestError(message);
      showError(message);
    } finally {
      setTestSending(false);
    }
  };

  const activeTenantHint = useMemo(() => {
    if (!isSuperAdmin || tenantId) {
      return null;
    }
    return 'Сейчас показаны landings по всем активным tenant. Можно выбрать конкретную клинику сверху.';
  }, [isSuperAdmin, tenantId]);

  // 23.03.26: catalog UI и runtime preview должны читать один backend definition source.
  const selectedItem = items.find((item) => item.key === selectedKey) ?? null;

  return (
    <MainLayout headerTitle="OKDoc лендинги" disableMainScroll>
      <div className="flex h-full min-h-0 flex-col">
      <div className="shrink-0 space-y-2 border-b border-shell-border bg-shell-surface px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          {isSuperAdmin && (
            <div className="w-full max-w-xs">
              <TenantSelector value={selectedTenantId || ''} onChange={setSelectedTenant} showLabel={false} />
            </div>
          )}
          <div className="min-w-[220px] flex-1">
            <ServerSearchInput
              value={search}
              onChange={setSearch}
              placeholder="Поиск по landing, шаблону, ID или binding key..."
              hint="Серверный поиск: ключ из preview URL или часть названия шаблона."
            />
          </div>
          <div className="w-44">
            <FilterSelect value={flow} onChange={setFlow} options={flowOptions} allOptionLabel="Все flow" placeholder="Все flow" />
          </div>
          <button type="button" className="shell-button-secondary h-9" onClick={() => setFiltersOpen((open) => !open)}>Фильтры</button>
          <button type="button" className="shell-button-secondary h-9" onClick={() => setShowGuide((open) => !open)}>Справка</button>
        </div>
        {filtersOpen && (
          <div className="grid grid-cols-1 gap-2 md:grid-cols-4">
            <FilterSelect value={scenarioKind} onChange={setScenarioKind} options={OKDOC_SCENARIO_KIND_OPTIONS} allOptionLabel="Все сценарии" placeholder="Все сценарии" />
            <FilterSelect value={sourceContext} onChange={setSourceContext} options={OKDOC_SOURCE_CONTEXT_OPTIONS} allOptionLabel="Все source" placeholder="Все source" />
            <FilterSelect value={originType} onChange={setOriginType} options={originTypeOptions} allOptionLabel="Все origin" placeholder="Все origin" />
            <FilterSelect value={bindingScope} onChange={setBindingScope} options={bindingScopeOptions} allOptionLabel="Все binding scope" placeholder="Все binding scope" />
          </div>
        )}
        <p className="text-xs leading-4 text-shell-muted">
          Публичный okdoc.su/&lt;code&gt; появляется только при отправке SMS. Здесь preview без короткой ссылки пациента.
          {activeTenantHint ? ` ${activeTenantHint}` : ''}
        </p>
      </div>
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-3">
        {showGuide && <FlowNote id="okdocLanding" />}
        {selectedItem && (
          <div className="rounded-xl border border-shell-border bg-shell-surface px-4 py-3">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-shell-strong">{selectedItem.display_name || selectedItem.name}</p>
                <p className="mt-1 text-xs leading-4 text-shell-muted">
                  {selectedItem.channel || 'generic'} · {OKDOC_LANDING_BINDING_SCOPE_LABELS[selectedItem.binding_scope || ''] || selectedItem.binding_scope || 'без scope'} · {selectedItem.template_name || selectedItem.cluster_name || 'без привязки'}
                </p>
                <p className="mt-2 max-w-3xl text-sm leading-5 text-shell-text">{selectedItem.prompt_text || 'Текст prompt не задан.'}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="secondary" size="sm" onClick={() => openPreview(selectedItem.key)}>Preview</Button>
                {selectedItem.definition_id && (
                  <Button variant="secondary" size="sm" onClick={() => openEditor(selectedItem)}>Редактировать</Button>
                )}
                <Button variant="secondary" size="sm" onClick={() => openPreview(selectedItem.key)}>Тест себе</Button>
              </div>
            </div>
          </div>
        )}
        <Card>
          <CardContent className="pt-6">
            {loading ? (
              <LoadingState />
            ) : items.length === 0 ? (
              <EmptyState
                type="noData"
                title="Каталог пока пуст"
                description="Для текущих фильтров не найдено ни DB bindings, ни legacy preset fallback."
              />
            ) : (
              <div className="space-y-4">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Landing</TableHead>
                      <TableHead>Flow</TableHead>
                      <TableHead>Источник</TableHead>
                      <TableHead>Scope</TableHead>
                      <TableHead>Template / cluster</TableHead>
                      <TableHead>Канал</TableHead>
                      <TableHead>Prompt</TableHead>
                      <TableHead>Действия</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((item) => (
                      <TableRow key={item.key} className={`cursor-pointer ${item.key === selectedKey ? 'bg-shell-primary-soft' : ''}`} onClick={() => setSelectedKey(item.key)}>
                        <TableCell>
                          <div className="space-y-1">
                            <div className="font-medium text-neutral-900">{item.display_name || item.name}</div>
                            <div className="flex flex-wrap gap-2">
                              <Badge variant={item.resolution_source === 'database' ? 'info' : 'warning'}>
                                {OKDOC_LANDING_RESOLUTION_SOURCE_LABELS[item.resolution_source] || item.resolution_source}
                              </Badge>
                              {!item.is_active && <Badge variant="error">inactive</Badge>}
                              {item.variant && <Badge variant="secondary">{item.variant}</Badge>}
                            </div>
                            <div className="text-xs text-neutral-500">
                              {item.tenant_name}
                              {item.tenant_slug ? ` / ${item.tenant_slug}` : ''}
                              {item.updated_at ? ` / ${formatDateTime(item.updated_at)}` : ''}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1 text-sm">
                            <div>{OKDOC_FLOW_LABELS[item.flow as keyof typeof OKDOC_FLOW_LABELS] || item.flow}</div>
                            <div className="text-xs text-neutral-500">{getOkdocScenarioKindLabel(item.scenario_kind)}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1 text-sm">
                            <div>{getOkdocSourceContextLabel(item.source_context)}</div>
                            <div className="text-xs text-neutral-500">
                              {OKDOC_LANDING_ORIGIN_LABELS[item.origin_type] || item.origin_type}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">{OKDOC_LANDING_BINDING_SCOPE_LABELS[item.binding_scope || ''] || item.binding_scope || '—'}</div>
                          {item.trigger_type && <div className="text-xs text-neutral-500">{item.trigger_type}</div>}
                        </TableCell>
                        <TableCell>
                          <BindingTargetDetails item={item} />
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">{item.channel || 'generic'}</div>
                        </TableCell>
                        <TableCell>
                          <div className="max-w-xs text-sm text-neutral-700">{item.prompt_text || '—'}</div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-2">
                            <Button variant="secondary" size="sm" onClick={() => openPreview(item.key)}>
                              <Eye className="mr-1 h-4 w-4" />
                              Preview
                            </Button>
                            {item.definition_id && (
                              <Button variant="secondary" size="sm" onClick={() => openEditor(item)}>
                                <Pencil className="mr-1 h-4 w-4" />
                                Редактировать
                              </Button>
                            )}
                            <Button variant="secondary" size="sm" onClick={() => openPreview(item.key)}>
                              <FlaskConical className="mr-1 h-4 w-4" />
                              Тест себе
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                <Pagination
                  offset={offset}
                  limit={PAGE_SIZE}
                  total={total}
                  onOffsetChange={setOffset}
                />
              </div>
            )}
          </CardContent>
        </Card>

        <Modal
          isOpen={Boolean(previewKey)}
          onClose={() => {
            setSearchParams((current) => {
              const next = new URLSearchParams(current);
              next.delete('preview');
              return next;
            });
            setPreviewKey(null);
            setPreview(null);
            setTestError(null);
            setTestMessage(null);
          }}
          title={preview?.item?.display_name || 'Preview landing'}
          size="xl"
        >
          {previewLoading ? (
            <LoadingState />
          ) : preview ? (
            <div className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <Card>
                  <CardContent className="space-y-2 pt-6 text-sm">
                    <div>
                      <span className="font-medium text-neutral-900">Flow:</span>{' '}
                      {OKDOC_FLOW_LABELS[preview.item.flow as keyof typeof OKDOC_FLOW_LABELS] || preview.item.flow}
                    </div>
                    <div>
                      <span className="font-medium text-neutral-900">Scenario:</span>{' '}
                      {getOkdocScenarioKindLabel(preview.item.scenario_kind)}
                    </div>
                    <div>
                      <span className="font-medium text-neutral-900">Source:</span>{' '}
                      {getOkdocSourceContextLabel(preview.item.source_context)}
                    </div>
                    <div>
                      <span className="font-medium text-neutral-900">Prompt:</span> {preview.item.prompt_text || '—'}
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="space-y-2 pt-6 text-sm">
                    <div>
                      <span className="font-medium text-neutral-900">Template:</span> {getBindingTargetCopy(preview.item).primary}
                    </div>
                    <div>
                      <span className="font-medium text-neutral-900">Cluster:</span> {getBindingTargetCopy(preview.item).secondary}
                    </div>
                    <div>
                      <span className="font-medium text-neutral-900">Scope:</span>{' '}
                      {OKDOC_LANDING_BINDING_SCOPE_LABELS[preview.item.binding_scope || ''] || preview.item.binding_scope || '—'}
                    </div>
                    <div>
                      <span className="font-medium text-neutral-900">Preview source:</span>{' '}
                      {OKDOC_LANDING_RESOLUTION_SOURCE_LABELS[preview.item.resolution_source] || preview.item.resolution_source}
                    </div>
                    <div>
                      <span className="font-medium text-neutral-900">Admin preview URL:</span>{' '}
                      <code className="break-all text-xs">
                        /okdoc-landings?preview={encodeURIComponent(preview.item.key)}
                      </code>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <OkdocLandingDefinitionPreview
                flow={preview.item.flow}
                tenantName={preview.item.tenant_name}
                tenantSlug={preview.item.tenant_slug}
                scenarioProfile={preview.scenario_profile}
                tenantClinic={{
                  clinic_name: preview.clinic_name,
                  clinic_phone: preview.clinic_phone,
                  clinic_address: preview.clinic_address,
                  clinic_website: preview.clinic_website,
                }}
              />
              {!preview.clinic_phone?.trim() &&
                !preview.clinic_address?.trim() &&
                !preview.clinic_website?.trim() && (
                  <p className="text-xs text-amber-800">
                    В настройках тенанта не заданы телефон, адрес и сайт — блок «Контакты клиники» в превью скрыт.
                    Заполните их в админке (настройки клиники / tenants.config).
                  </p>
                )}

              <Card className="border-violet-200 bg-violet-50">
                <CardContent className="space-y-4 pt-6">
                  <div>
                    <div className="flex items-center gap-2 text-sm font-semibold text-violet-900">
                      <FlaskConical className="h-4 w-4" />
                      <span>Тест себе</span>
                    </div>
                    <p className="mt-2 text-xs leading-5 text-violet-800">
                      Сообщение соберётся заново для этого landing и уйдёт на твой тестовый контакт. Для каждого запуска
                      создаётся новый OKDoc shortlink, он не переиспользует боевой код пациента. Если SMS возвращает
                      ошибку провайдера или sender не одобрен, попробуйте WhatsApp / Telegram / MAX.
                    </p>
                  </div>

                  <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-4">
                    {(preview.test_channels || []).map((item) => (
                      <button
                        key={item.channel}
                        type="button"
                        onClick={() => {
                          if (item.available) {
                            setTestChannel(item.channel);
                            setTestError(null);
                          }
                        }}
                        disabled={!item.available}
                        className={`rounded-lg border px-3 py-3 text-left text-xs transition-colors ${
                          testChannel === item.channel
                            ? 'border-violet-400 bg-white text-violet-900'
                            : 'border-violet-200 bg-white/70 text-neutral-700'
                        } ${!item.available ? 'cursor-not-allowed opacity-60' : 'hover:border-violet-300'}`}
                      >
                        <div className="font-medium">{item.label}</div>
                        <div className="mt-1 text-neutral-500">
                          {item.available
                            ? `${item.target_label || 'контакт'}: ${item.target_value || 'готово'}`
                            : item.reason || 'Канал недоступен'}
                        </div>
                      </button>
                    ))}
                  </div>

                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="text-xs text-violet-800">
                      Будет отправлен текст вида: <code>{preview.item.prompt_text || preview.item.display_name || 'Landing'}</code> + новый shortlink.
                    </div>
                    <Button onClick={sendTestLanding} isLoading={testSending} disabled={!testChannel}>
                      Отправить тест себе
                    </Button>
                  </div>

                  {testError && <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{testError}</div>}
                  {testMessage && (
                    <div className="rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-xs text-green-700">{testMessage}</div>
                  )}
                </CardContent>
              </Card>
            </div>
          ) : (
            <EmptyState
              type="noData"
              title="Preview недоступен"
              description="Не удалось загрузить данные landing."
            />
          )}
        </Modal>

        <Modal
          isOpen={Boolean(editingItem)}
          onClose={() => {
            if (editorSaving) {
              return;
            }
            setEditingItem(null);
            setEditingDefinition(null);
          }}
          title={editingItem?.display_name || 'Редактирование landing'}
          size="xl"
          closeOnBackdropClick={false}
        >
          {editorLoading || !editingItem ? (
            <LoadingState />
          ) : editingDefinition ? (
            <div className="space-y-4">
              <OkdocLandingDefinitionEditor
                tenantId={editingItem.tenant_id}
                tenantName={editingItem.tenant_name}
                tenantSlug={editingItem.tenant_slug}
                value={editingDefinition}
                onChange={setEditingDefinition}
              />

              <div className="flex justify-end gap-3">
                <Button
                  variant="secondary"
                  onClick={() => {
                    if (editorSaving) return;
                    setEditingItem(null);
                    setEditingDefinition(null);
                  }}
                >
                  Закрыть
                </Button>
                <Button onClick={saveDefinition} isLoading={editorSaving}>
                  Сохранить
                </Button>
              </div>
            </div>
          ) : (
            <EmptyState
              type="noData"
              title="Definition недоступен"
              description="Не удалось загрузить данные для редактирования."
            />
          )}
        </Modal>
      </div>
      </div>
    </MainLayout>
  );
}
